import { useState, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../backend/convex/_generated/api';
import { extractChapterContent, replaceChapterContent } from '@/components/publisher/chapterUtils';
import type { ChapterEdit, ChapterUpdate, PreviewData } from '@/types';

interface UseChapterEditorReturn {
  editingChapter: ChapterEdit | null;
  editingChapterIndex: number | null;
  handleChapterClick: (chapterIndex: number, markdown: string, preview: PreviewData | null) => void;
  handleChapterSave: (
    updates: ChapterUpdate,
    markdown: string,
    onMarkdownChange: (value: string) => void,
    onPreviewUpdate: (preview: PreviewData) => void
  ) => Promise<void>;
  handleChapterCancel: () => void;
  handlePrevChapter: () => void;
  handleNextChapter: (totalChapters: number) => void;
}

export function useChapterEditor(): UseChapterEditorReturn {
  const [editingChapter, setEditingChapter] = useState<ChapterEdit | null>(null);
  const [editingChapterIndex, setEditingChapterIndex] = useState<number | null>(null);
  
  const previewMutation = useMutation(api.publisher.preview);

  const handleChapterClick = useCallback((chapterIndex: number, markdown: string, preview: PreviewData | null) => {
    if (!preview || !preview.chapters[chapterIndex]) return;
    
    const chapterData = preview.chapters[chapterIndex];
    const extracted = extractChapterContent(markdown, chapterIndex);
    
    if (!extracted) return;
    
    setEditingChapterIndex(chapterIndex);
    setEditingChapter({
      _id: `chapter-${chapterIndex}`,
      sequence: chapterData.sequence,
      title: extracted.title,
      tokenCost: chapterData.tokenCost,
      content: extracted.content,
      wordCount: chapterData.wordCount,
      readTime: chapterData.readTime,
      llmTokens: chapterData.llmTokens
    });
  }, []);

  const handleChapterSave = useCallback(async (
    updates: ChapterUpdate,
    markdown: string,
    onMarkdownChange: (value: string) => void,
    onPreviewUpdate: (preview: PreviewData) => void
  ) => {
    if (editingChapterIndex === null) return;
    
    const newMarkdown = replaceChapterContent(
      markdown,
      editingChapterIndex,
      updates.title,
      updates.content
    );
    
    onMarkdownChange(newMarkdown);
    
    if (newMarkdown.trim().length > 0) {
      const result = await previewMutation({ markdown: newMarkdown });
      onPreviewUpdate(result);
    }
    
    setEditingChapter(null);
    setEditingChapterIndex(null);
  }, [editingChapterIndex, previewMutation]);

  const handleChapterCancel = useCallback(() => {
    setEditingChapter(null);
    setEditingChapterIndex(null);
  }, []);

  const handlePrevChapter = useCallback(() => {
    // This needs to be called from the component with proper context
    // We'll handle the actual navigation in the component
  }, []);

  const handleNextChapter = useCallback(() => {
    // This needs to be called from the component with proper context
    // We'll handle the actual navigation in the component
  }, []);

  return {
    editingChapter,
    editingChapterIndex,
    handleChapterClick,
    handleChapterSave,
    handleChapterCancel,
    handlePrevChapter,
    handleNextChapter,
  };
}
